import {
  Box,
  Button,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { debounce } from "lodash-es";
import SearchIcon from "@mui/icons-material/Search";
import ToolbarSelect from "components/Common/ToolbarSelect";
import DateRangePicker from "components/Common/DateRangePicker";
import FileExportIcon from "@mui/icons-material/FileDownload";

const SEARCH_DEBOUNCE_MS = 400;

interface Props {
  title: string;
  filters: any;
  onFilterChange: (newFilters: any) => void;
  onReset: () => void;
  onExport: () => void;
}

const MessageToolbar: React.FC<Props> = ({
  title,
  filters,
  onFilterChange,
  onReset,
  onExport,
}) => {
  const [searchInput, setSearchInput] = useState(filters.SearchTerm ?? "");
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  useEffect(() => {
    setSearchInput(filters.SearchTerm ?? "");
  }, [filters.SearchTerm]);

  const debouncedSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        onFilterChangeRef.current({ SearchTerm: value });
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => debouncedSearchTerm.cancel(), [debouncedSearchTerm]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSearchTerm(value);
    },
    [debouncedSearchTerm],
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Search template"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ minWidth: 200, flex: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <ToolbarSelect
          label="ZNS Type"
          value={filters.ZnsCampaignType || "all"}
          onChange={(val) =>
            onFilterChange({ ZnsCampaignType: val === "all" ? undefined : val })
          }
          sx={{ m: 0, flex: 1 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="MANUAL">Manual</MenuItem>
          <MenuItem value="AUTO">Auto</MenuItem>
        </ToolbarSelect>

        <ToolbarSelect
          label="ZNS Purpose"
          value={filters.ZnsTemplateTag || "all"}
          onChange={(val) =>
            onFilterChange({ ZnsTemplateTag: val === "all" ? undefined : val })
          }
          sx={{ m: 0, flex: 1 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="TRANSACTION">TRANSACTION</MenuItem>
          <MenuItem value="CUSTOMER_CARE">CUSTOMER_CARE</MenuItem>
          <MenuItem value="PROMOTION">PROMOTION</MenuItem>
        </ToolbarSelect>

        <ToolbarSelect
          label="ZNS Platform"
          value={filters.Relation || "all"}
          onChange={(val) =>
            onFilterChange({ Relation: val === "all" ? undefined : val })
          }
          sx={{ m: 0, flex: 1 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="WithZnsCampaign">ZNS CMS</MenuItem>
          <MenuItem value="WithoutZnsCampaign">OL</MenuItem>
        </ToolbarSelect>

        <ToolbarSelect
          label="Status"
          value={filters.Status || "all"}
          onChange={(val) =>
            onFilterChange({ Status: val === "all" ? undefined : val })
          }
          sx={{ m: 0, flex: 1 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="Sent">Sent</MenuItem>
          <MenuItem value="Invalid">Invalid</MenuItem>
          <MenuItem value="Error">Error</MenuItem>
        </ToolbarSelect>

        <Box sx={{ m: 1, display: "flex", alignItems: "center" }}>
          <DateRangePicker
            label="Send Date"
            value={{
              from: filters.SendDateFrom || "",
              to: filters.SendDateTo || "",
            }}
            onChange={(from, to) =>
              onFilterChange({ SendDateFrom: from, SendDateTo: to })
            }
            format="DD/MM/YYYY"
            size="small"
            minWidth={220}
          />
        </Box>

        <Button
          variant="outlined"
          onClick={onReset}
          sx={{
            height: 40,
            borderColor: "grey.400",
            color: "text.primary",
            fontWeight: "bold",
            ml: 1,
          }}
        >
          RESET
        </Button>
        <Button
          variant="contained"
          onClick={onExport}
          sx={{
            height: 40,
            fontWeight: "bold",
            ml: 1,
            gap: 0.5,
          }}
        >
          <FileExportIcon fontSize="small" /> EXPORT
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(MessageToolbar);
